/**
 * The featured hero's view model (spec §4.2, §4.3). Pure and serialisable: the postbuild script renders it
 * into the static HTML, and the browser re-derives it from the same data with the current clock (K-2, K-7).
 */
import { actionFor, badgeFor, isBookable, nextDate, sessionState, upcomingSessions } from "./availability";
import { dateTile, fmtDay, fmtWhen } from "./time";
import { gateLink, rsvpTarget } from "./gate";

export const MAX_FEATURED = 5;

export function placeOf(s) {
  if (!s) return null;              // an event whose dates have all passed has no session to place
  if (s.format === "online" || !s.venue) return "Online";
  const city = [s.venue.city, s.venue.region].filter(Boolean).join(", ");
  const base = s.venue.name ? `${s.venue.name} · ${city}` : city;
  return s.format === "hybrid" ? `${base} · and online` : base;
}

export function slideFor(event, now = new Date()) {
  const cancelled = event.status === "cancelled";
  const s = nextDate(event, now);
  const bookable = cancelled ? [] : upcomingSessions(event, now).filter((x) => isBookable(x, event, now));
  const single = bookable.length === 1 ? bookable[0] : null;
  const action = single ? actionFor(single, event, now) : { kind: "none" };
  return {
    slug: event.slug,
    title: event.title,
    tagline: event.tagline ?? null,
    type: event.type ? { name: event.type.name, hue: event.type.hue } : null,
    cancelled,
    date: s
      ? {
          event_session_id: s.event_session_id,
          starts_at: s.starts_at,
          when: fmtWhen(s),
          opensLabel: s.rsvp_opens_at ? fmtDay(s.rsvp_opens_at, s.timezone) : null,
          tile: dateTile(s.starts_at, s.timezone),
          place: placeOf(s),
          online: s.format === "online",
          badge: badgeFor(s, event, now),
          window: { rsvp_opens_at: s.rsvp_opens_at, rsvp_closes_at: s.rsvp_closes_at, ends_at: s.ends_at },
        }
      : null,
    image: event.images?.hero ? { url: event.images.hero.url, alt: event.images.hero.alt } : null,
    /** The event session shown, for the browser-side "your time" line on online and hybrid dates (§3.7 Times). */
    local: s && s.format !== "in_person" ? { starts_at: s.starts_at, ends_at: s.ends_at, timezone: s.timezone } : null,
    viewHref: `/events/${event.slug}`,
    /** Only when the event has exactly one bookable date (§4.2). */
    rsvp: single && action.kind === "rsvp"
      ? { href: gateLink(rsvpTarget(event.slug, single.event_session_id)), label: action.label, event_session_id: single.event_session_id }
      : null,
  };
}

/**
 * Which events lead the hero: the shared event first (share view), then the featured list in rank order,
 * at most 5 featured, no duplicates, public only from the list. A withdrawn/unknown share falls back to the landing (§4.3).
 */
export function buildHero(list, shared, now = new Date(), catalog = null) {
  const events = [];
  if (shared) events.push(shared);
  // Their question, 2026-09-15: is the carousel driven by `featured` or by the next bookable session? It is
  // driven by whatever /catalog?featured=1 returns, in featured.rank order — a null `featured` does not exclude
  // an event, but an empty featured response leaves the hero blank. With one published event that is an absurd
  // page, so when nothing is featured we lead with the soonest bookable public event from the full catalogue.
  const source = (list?.items ?? []).length ? (list?.items ?? []) : soonestFirst(catalog?.items ?? [], now);
  const featured = source
    .filter((e) => e.visibility === "public")
    .sort((a, b) => (a.featured?.rank ?? 1e9) - (b.featured?.rank ?? 1e9))
    .slice(0, MAX_FEATURED);
  for (const e of featured) if (!events.some((x) => x.slug === e.slug)) events.push(e);
  const shown = events.map((event) => ({ event, session: nextDate(event, now) }));
  return { slides: events.map((e) => slideFor(e, now)), next: nextRsvp(events, now), shown, fallback: list?.fallback === true, stale: list?.stale === true };
}

/** Public events with a future date, soonest first — the hero's fallback when nothing is featured. */
function soonestFirst(items, now) {
  return (items || [])
    .filter((e) => e && e.visibility === "public" && e.status !== "cancelled" && nextDate(e, now))
    .sort((a, b) => nextDate(a, now).starts_at.localeCompare(nextDate(b, now).starts_at));
}

/** The soonest bookable date among the events shown, for "RSVP to the next event" (§4.1). */
export function nextRsvp(events, now = new Date()) {
  let best = null;
  for (const event of events) {
    if (event.status === "cancelled") continue;
    for (const s of upcomingSessions(event, now)) {
      if (!isBookable(s, event, now)) continue;
      if (!best || s.starts_at < best.s.starts_at) best = { event, s };
    }
  }
  if (!best) return null;
  const action = actionFor(best.s, best.event, now);
  if (action.kind !== "rsvp") return null;
  return {
    slug: best.event.slug, title: best.event.title, event_session_id: best.s.event_session_id,
    href: gateLink(rsvpTarget(best.event.slug, best.s.event_session_id)), label: action.label,
    window: { rsvp_opens_at: best.s.rsvp_opens_at, rsvp_closes_at: best.s.rsvp_closes_at, ends_at: best.s.ends_at },
  };
}

/** Does this date still accept an RSVP right now? (K-7 re-check.) */
export const stillBookable = (event, s, now = new Date()) => sessionState(s, event, now) === "scheduled" && isBookable(s, event, now);
